import StyledModal from '@/components/StyledModal';
import { Button, Checkbox, Flex, ModalBody, ModalFooter, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (deletedOption: EventOptions | null) => void;
  event?: any;
  isSameDay?: boolean;
  isAllowThisEventOption?: boolean;
  onlyThisOccurrenceEdit?: boolean;
  mode?: 'edit' | 'delete';
};

export enum EventOptions {
  THIS_EVENT = 'this_event',
  THIS_AND_FOLLOWING_EVENTS = 'this_and_following_events',
  ALL_EVENTS = 'all_events',
}

const RecurringEventModal = ({
  isOpen,
  onCancel,
  onConfirm,
  isSameDay,
  isAllowThisEventOption = true,
  onlyThisOccurrenceEdit = false,
  mode = 'delete',
}: Props) => {
  const { t } = useTranslation('DeleteActionModal');
  const [selectedOption, setSelectedOption] = useState<EventOptions | null>(null);
  const isEdit = mode === 'edit';
  const displayTitle = t('recurring.title');

  useEffect(() => {
    if (!isOpen) return;
    if (onlyThisOccurrenceEdit && isEdit) {
      setSelectedOption(EventOptions.THIS_EVENT);
    } else if (!onlyThisOccurrenceEdit) {
      setSelectedOption(null);
    }
  }, [isOpen, onlyThisOccurrenceEdit, isEdit]);

  const handleCheckboxChange = (option: EventOptions) => {
    setSelectedOption(option);
  };

  return (
    <StyledModal isOpen={isOpen} onClose={onCancel} size="xl" title={displayTitle}>
      <ModalBody>
        <Flex flexDirection="column" gap={2} marginTop={3}>
          {isAllowThisEventOption && (
            <Checkbox
              isChecked={selectedOption === EventOptions.THIS_EVENT}
              onChange={() => handleCheckboxChange(EventOptions.THIS_EVENT)}
              size="sm"
              variant="round"
            >
              <Text as="span" textStyle="large">
                {isEdit ? t('recurringEdit.optionThisOnly') : t('recurringDelete.optionThisOnly')}
              </Text>
            </Checkbox>
          )}
          {!onlyThisOccurrenceEdit && (
            <Checkbox
              isChecked={selectedOption === EventOptions.THIS_AND_FOLLOWING_EVENTS}
              onChange={() => handleCheckboxChange(EventOptions.THIS_AND_FOLLOWING_EVENTS)}
              size="sm"
              variant="round"
            >
              <Text as="span" textStyle="large">
                {isEdit ? t('recurringEdit.optionThisAndFollowing') : t('recurringDelete.optionThisAndFollowing')}
              </Text>
            </Checkbox>
          )}
          {isSameDay && !onlyThisOccurrenceEdit && (
            <Checkbox
              isChecked={selectedOption === EventOptions.ALL_EVENTS}
              onChange={() => handleCheckboxChange(EventOptions.ALL_EVENTS)}
              size="sm"
              variant="round"
            >
              <Text as="span" textStyle="large">
                {isEdit ? t('recurringEdit.optionAll') : t('recurringDelete.optionAll')}
              </Text>
            </Checkbox>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel} size="lg" variant="secondary">
          {t('buttons.cancel')}
        </Button>
        <Button
          isDisabled={!selectedOption}
          onClick={() => onConfirm(selectedOption || null)}
          size="lg"
          variant={isEdit ? 'primary' : 'danger'}
        >
          {isEdit ? t('buttons.update') : t('buttons.delete')}
        </Button>
      </ModalFooter>
    </StyledModal>
  );
};

export default RecurringEventModal;
